import type { PriceItemType } from 'core/accounting/domain/PriceItemType'
import type { Option } from 'core/domain/Option'
import type { Stop, StopType } from 'core/domain/Stop'

export type ShipmentChargeStatus = 'pending' | 'approved' | 'rejected' | 'canceled'
export type ShipmentChargeAction = 'approve' | 'reject' | 'cancel'
export type ShipmentChargeEventType = 'creation' | 'approval' | 'rejection' | 'cancellation'
export type ShipmentChargeSource = 'quote' | 'carrier' | 'shipper'

export type ShipmentChargeType = {
  uuid: string
  code: string
  name: string
  isAccessorial: boolean
  stopRelationship: Array<StopType>
}

export type ShipmentChargeAttachment = {
  fileName: string
  fileUrl: string
}

export type ShipmentChargeEvent = {
  createdAt: string
  performedBy: string
  description: string | null
}

export type ShipmentCharge = {
  uuid: string
  type?: ShipmentChargeType
  priceItemType?: PriceItemType
  createdAt?: string
  createdBy?: string
  canceledBy?: string
  approvedBy?: string
  rejectedBy?: string
  status: { value: ShipmentChargeStatus; label: string }
  quantity: number
  unitPrice: number
  total: number
  stop?: Stop
  attachment?: ShipmentChargeAttachment
  description?: string
  rejectReason?: string
  cancelReason?: string
  canBeCanceled: boolean
}

export type AccessorialOption = Option<string> & {
  stopsRelationship?: PriceItemType['stopsRelationship']
}

export type NewShipmentCharge = {
  chargeType?: AccessorialOption | null
  priceItemType?: AccessorialOption | null
  quantity: number
  unitPrice?: number
  stop: Option<string | number> | null
  description: string
  attachmentFile?: File
}

export type ShipmentAccountingSummary = {
  quotedTotal: number
  pendingCharges: number
  approvedCharges: number
  total: number
}

export const CHARGE_DESCRIPTION_MAX_CHARS = 150
export const CHARGE_ACCEPTED_FILE_EXTENSIONS = ['.jpeg', '.jpg', '.png', '.pdf']
export const CHARGE_MAX_FILE_SIZE_IN_MB = 10

export function calculateTotalPrice({
  quantity,
  unitPrice,
}: Pick<NewShipmentCharge, 'quantity' | 'unitPrice'>) {
  return unitPrice ? quantity * unitPrice : 0
}

export function validateAttachmentFile(file: File) {
  const fileSizeInMB = (file.size || 0) / (1024 * 1024)
  const [fileExtension] = file.name.split('.').slice(-1)
  const fileSizeIsAllowed = fileSizeInMB <= CHARGE_MAX_FILE_SIZE_IN_MB
  const extensionIsAllowed = CHARGE_ACCEPTED_FILE_EXTENSIONS.includes(
    `.${fileExtension.toLowerCase()}`
  )

  return { fileSizeIsAllowed, extensionIsAllowed }
}

export function updateShipmentCharge(charge: ShipmentCharge, newCharge: ShipmentCharge) {
  if (charge.uuid === newCharge.uuid) {
    return newCharge
  }
  return charge
}

export function isChargeStatusOutdated(charge?: ShipmentCharge): boolean {
  return Boolean(charge && charge.status.value !== 'pending')
}

export const ModeToFullMode = {
  FTL: 'full_truck_load',
  LTL: 'less_than_truckload',
  IMDL: 'intermodal',
  DRAY: 'drayage',
  PTL: 'partial_truckload',
  EXP: 'expedited',
  VLTL: 'volume_less_than_truckload',
}
